import { Injectable, inject } from '@angular/core'
import { environment } from 'src/environments/environment'
import { authEndpoints } from '../../constants/valueConstants'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'
import { IUser } from '@app/core/models/user.model'
import { UserStore } from '@core/services/user/user.store'
import { PermissionService } from '@core/services/permission-service/permission-service'
import { AppConstants } from '@app/core/constants/app.constants'
import { IndexDBHelper } from '../indexdb-service/indexdb-helper.service'
import { UserHierarchyService } from '@core/services/user-hierarchy/user-hierarchy.service'

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private http = inject(HttpClient)
    private userStore = inject(UserStore)
    private permissionService = inject(PermissionService)
    private indexDBHelper = inject(IndexDBHelper)
    private userHierarchyService = inject(UserHierarchyService)

    async isAuthenticated(): Promise<boolean> {
        try {
            const resp = await firstValueFrom(
                this.http.get(environment.apiBaseUrl + authEndpoints.USER_INFO_PATH, {
                    withCredentials: true,
                    observe: 'response',
                }),
            )
            this.userStore.setUserData(resp.body as IUser)
            return !!resp && resp.status === 200
        } catch (error) {
            console.error('Auth service - Token validation error:', error)
            return false
        }
    }

    async getUserInfo(version: number): Promise<IUser> {
        const resp = await firstValueFrom(
            this.http.get(
                `${environment.apiBaseUrl}${authEndpoints.USER_INFO_PATH}?appId=${AppConstants.SUB_APP_ID}&permissionVersion=${version}`,
                {
                    withCredentials: true,
                    observe: 'response',
                },
            ),
        )
        this.userStore.setUserData(resp.body as IUser)
        return resp.body as IUser
    }

    async logout(): Promise<void> {
        try {
            await firstValueFrom(
                this.http.post(
                    environment.apiBaseUrl + authEndpoints.LOGOUT_PATH,
                    {},
                    { withCredentials: true },
                ),
            )
        } catch (error) {
            console.error('Auth service - Logout error:', error)
        } finally {
            this.userStore.clearUserData()
            this.userHierarchyService.clearCache()
            this.permissionService.clearCache()
            try {
                await this.indexDBHelper.clearPermissions()
            } catch {
                // Best-effort cleanup; logout should not fail due to storage issues.
            }
        }
    }
}
